import { useTheme } from '../state/useTheme';
import { useStrings } from '../state/useLocale';
import { sound } from '../lib/sound';

/**
 * The switch between the two grounds.
 *
 * It sits in the corner cluster rather than in a settings screen, for the
 * reason the sound control does: there is no settings screen in this product,
 * and somebody reading this on a projector, or in a bright room, or under a
 * screen policy, needs it from wherever they are — including the middle of a
 * level. The cluster runs inward from the corner in the order of how often
 * each is wanted: the chat bubble owns it at 14px, the sound control steps
 * inside at 70px, and this one sits at 142px. The 106px slot between them is
 * the language switch's, and is deliberately left for it.
 *
 * Dark is the default and stays the default. Nothing here consults
 * `prefers-color-scheme` — lib/theme.ts says why at length — and the title
 * names which of the two is the default, so that somebody who hit this by
 * accident can tell what they switched away from. That word is the whole
 * difference between a preference and a mistake.
 *
 * The four strings are English in the markup, the way every string in App.tsx
 * still is, including the sound control's. They move into the catalogue when
 * the language layer lands and converts the corner cluster as a whole; the
 * Arabic for them is written and waiting in lib/strings/app.ts under `theme`.
 *
 * No `dir` attribute, and that is load-bearing rather than an omission:
 * `inset-inline-end` resolves against the element's *own* direction, so a
 * `dir` on a `position: fixed` control moves the control. This one can go
 * without because it never renders a word — only a glyph — which is exactly
 * what forces `dir` onto the language switch beside it.
 */
export function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const s = useStrings();
  const dark = theme === 'dark';

  return (
    <button
      className="theme"
      type="button"
      title={dark ? s.app.theme.titleDark : s.app.theme.titleLight}
      aria-label={dark ? s.app.theme.labelDark : s.app.theme.labelLight}
      onClick={() => {
        sound.click();
        toggle();
      }}
    >
      <svg viewBox="0 0 16 16" aria-hidden="true" width="13" height="13">
        {/* A disc with one half filled — the ordinary sign for contrast, and
            the only honest one here. A sun and a moon would be saying
            something about the time of day, which is not what this changes,
            and there is no weather in this product.

            Drawn as an outline plus a filled half rather than as two arcs, so
            that at 13px it is one circle and not two crescents failing to
            meet. The half that is filled is the half the page is about to
            become, which makes the button a preview as well as a switch. */}
        <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="1.3" />
        <path d={dark ? 'M8 2a6 6 0 0 0 0 12z' : 'M8 2a6 6 0 0 1 0 12z'} fill="currentColor" stroke="none" />
      </svg>
    </button>
  );
}
